import { FunctionComponent } from 'react'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'
import CustomModal from '../CustomModal'
import SvgFragment from '../../../fragments/SvgFragment'
import { styles } from '../../../styles/themes/style'
import { useAppHandlers } from '../../../services/actions/app/useAppHandlers'
import { useAuthStore } from '../../../stores/auth/useAuthStore'
import { CAMPAIGNS_SETUP_CALL_URL } from '../../../services/campaigns/campaignsNavAnnouncement'
import { dismissCampaignsBulkActionAnnouncement } from '../../../services/campaigns/campaignsBulkActionAnnouncement'
import { AppRoutes } from '../../../interfaces/store/appStore'

interface ICampaignsAnnouncementModalProps {
    onContinue?: () => void
}

const features = [
    {
        icon: 'userPlusLucide',
        title: 'Send connection requests automatically',
        text: 'Build your network while you focus on what matters.'
    },
    {
        icon: 'messageSquareTextLucide',
        title: 'Message leads in bulk, personalized by AI',
        text: 'Start meaningful conversations that get replies.'
    },
    {
        icon: 'heartLucide',
        title: "Engage on your prospects' posts",
        text: 'Stay top of mind with smart, timely interactions.'
    },
    {
        icon: 'cloudLucide',
        title: 'Run 24/7 from the LeadDelta Cloud',
        text: "Campaigns run around the clock, even when you're offline."
    }
]

const CampaignsAnnouncementModal: FunctionComponent<ICampaignsAnnouncementModalProps> = ({ onContinue }) => {
    const { hideModal } = useAppHandlers()
    const { store: { user } } = useAuthStore()
    const navigate = useNavigate()

    // hideModal must run before onContinue — onContinue may open the next modal.
    const closeHandler = () => {
        if (user?._id) dismissCampaignsBulkActionAnnouncement(user._id)
        hideModal()
        onContinue?.()
    }

    // Leaving for billing: dismiss and close, but skip onContinue — the user is navigating away.
    const openBilling = () => {
        if (user?._id) dismissCampaignsBulkActionAnnouncement(user._id)
        hideModal()
        navigate(AppRoutes.BILLING)
    }

    return (
        <CustomModal
            headingText={
                <HeadingRow>
                    <IconBadge>
                        <SvgFragment type="sideCampaignsLucide" />
                    </IconBadge>
                    <HeadingText>
                        Say hello to Campaigns
                        <NewBadge>New</NewBadge>
                    </HeadingText>
                </HeadingRow>
            }
            closeButton
            onCancelHandler={() => {
                closeHandler()
                // returning false stops CustomModal's own hideModal, which would close a modal opened by onContinue
                return false
            }}
            confirmButton={{
                text: 'Book a meeting',
                bigButton: true,
                onClickHandler: () => {
                    window.open(CAMPAIGNS_SETUP_CALL_URL, '_blank', 'noopener,noreferrer')
                    closeHandler()
                }
            }}
        >
            <Body>
                <Paragraph>
                    <strong>Unlimited LinkedIn AI Campaigns</strong> are here. Put your LinkedIn
                    outreach on autopilot, all in your tone of voice.
                </Paragraph>
                <FeatureCard>
                    {features.map((feature) => (
                        <FeatureItem key={feature.icon}>
                            <IconChip>
                                <SvgFragment type={feature.icon} />
                            </IconChip>
                            <FeatureCopy>
                                <FeatureTitle>{feature.title}</FeatureTitle>
                                <FeatureText>{feature.text}</FeatureText>
                            </FeatureCopy>
                        </FeatureItem>
                    ))}
                </FeatureCard>
                <Paragraph>
                    Available on the <BillingLink onClick={openBilling}>Growth plan</BillingLink>. Book a call with our team
                    and we&apos;ll get you set up.
                </Paragraph>
            </Body>
        </CustomModal>
    )
}

const HeadingRow = styled.div`
    display: flex;
    align-items: center;
    gap: 14px;
`

const HeadingText = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
`

const IconBadge = styled.div`
    flex-shrink: 0;
    width: 44px;
    height: 44px;
    border-radius: 14px;
    background-color: ${styles.colors.primary600};
    box-shadow: 0 4px 12px rgba(89, 100, 255, 0.35);
    display: flex;
    align-items: center;
    justify-content: center;

    .svg { width: 22px; height: 22px; }
    .svg .path { stroke: ${styles.colors.white}; }
`

const NewBadge = styled.span`
    flex-shrink: 0;
    padding: 2px 9px;
    border-radius: 20px;
    background-color: ${styles.colors.primary100};
    color: ${styles.colors.primary600};
    border: 1px solid ${styles.colors.primary200};
    font-size: 11px;
    line-height: 16px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.8px;
`

const Body = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`

const Paragraph = styled.p`
    margin: 0;
    font-family: NunitoSans;
    font-size: 14px;
    line-height: 21px;
    color: ${({ theme: { nonaryColor } }) => nonaryColor};

    strong { color: ${({ theme: { primaryColor } }) => primaryColor}; }
`

const BillingLink = styled.a`
    color: ${styles.colors.primary600};
    font-weight: bold;
    cursor: pointer;

    &:hover {
        color: ${styles.colors.primary400};
        text-decoration: underline;
    }
`

const FeatureCard = styled.div`
    border-radius: 12px;
    border: 1px solid ${styles.colors.black200};
    padding: 4px 20px;
`

const FeatureItem = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 16px;
    padding: 14px 0;

    &:not(:last-child) {
        border-bottom: 1px solid ${styles.colors.black100};
    }
`

const IconChip = styled.div`
    flex-shrink: 0;
    width: 44px;
    height: 44px;
    border-radius: 50%;
    background-color: ${styles.colors.primary100};
    display: flex;
    align-items: center;
    justify-content: center;

    .svg { width: 20px; height: 20px; display: block; }
    .svg .path { stroke: #7B85F7; }
`

const FeatureCopy = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    font-family: NunitoSans;
`

const FeatureTitle = styled.span`
    font-size: 14px;
    line-height: 20px;
    font-weight: bold;
    color: ${({ theme: { primaryColor } }) => primaryColor};
`

const FeatureText = styled.span`
    font-size: 14px;
    line-height: 20px;
    color: ${({ theme: { nonaryColor } }) => nonaryColor};
`

export default CampaignsAnnouncementModal
